import websites from "./websites"

const getReferrer = () => document.referrer || null

const getInferredParams = (): { medium: string; source: string } => {
  const referrer = getReferrer()

  const mapped = {
    medium: "Organic",
    source: "Direct",
  }

  if (referrer) {
    const referrerURL = new URL(referrer)
    const chunks = referrerURL.hostname.split(".")
    let domain = ""
    if (chunks.length === 2) domain = chunks[0]

    if (chunks.length === 3) {
      domain = chunks[1]
      if (chunks[1] === "co") domain = chunks[0]
    }

    const obj = websites.find(({ id }) => id === domain)
    mapped.medium = obj?.medium || "Referral"
    mapped.source = obj?.source || "Referral"
  }

  return mapped
}

const getMappedParams = (params: Record<string, string | null>) => {
  const mappedParams = getInferredParams()

  if (params.source) {
    // @ts-expect-error can't make these properties optional
    delete mappedParams.source
  }

  if (params.medium) {
    // @ts-expect-error can't make these properties optional
    delete mappedParams.medium
  }

  return { ...params, ...mappedParams }
}

export const parseParam = (param: string | string[] | undefined | null) =>
  Array.isArray(param) ? param[0] : param ?? null

type Options = {
  includeReferrer?: boolean
}

const parseUTMParams = <
  T extends Record<string, string | string[] | undefined | null>
>(
  params: T,
  options?: Options
): Record<string, string | null> => {
  // If all values are null then return null
  // if (Object.values(params).every(e => e === null)) return null

  const cleaned: Record<string, string | null> = {}
  for (const [k, v] of Object.entries(params)) {
    cleaned[k] = parseParam(v)
  }

  if (options?.includeReferrer) {
    return { ...getMappedParams(cleaned), referrer: getReferrer() }
  }

  return getMappedParams(cleaned)
}

const getFallbackSubmissionPageUrl = () => {
  if (typeof window === "undefined") return ""

  try {
    if (window.parent && window.parent.location) {
      return window.parent.location.href
    }
  } catch (_) {
    return window.location.href
  }

  return window.location.href
}

export const getUTMParamsForMercury = (
  query: Record<string, any>,
  referrer: string | null
) =>
  parseUTMParams(
    {
      source: query.utm_source ?? null,
      medium: query.utm_medium ?? null,
      campaign: query.utm_campaign ?? null,
      campaignId: query.redirect_id ?? null,
      suraasaCampaignId: query.campaign_id ?? null,
      adsetId: query.adset_id ?? null,
      adId: query.ad_id ?? null,
      content: query.utm_content ?? null,
      audience: query.audience ?? null,
      term: query.utm_term ?? null,
      var: query.var ?? null,
      fbclid: query.fbclid ?? null,
      gclid: query.gclid ?? null,
      source_url: query.utm_source_url || referrer || null,
    },
    { includeReferrer: true }
  )

export const getUTMParamsForForm = (): Record<string, string | number> => {
  if (typeof window === "undefined") return {}

  const referrer = getReferrer()
  const utmSourceUrl = getParam("utm_source_url") || referrer || ""

  const inferredParams = getInferredParams()

  return {
    utm_source: getParam("utm_source") || inferredParams.source,
    utm_medium: getParam("utm_medium") || inferredParams.medium,
    utm_campaign: getParam("utm_campaign"),
    utm_content: getParam("utm_content"),
    utm_term: getParam("utm_term"),
    audience: getParam("audience"),
    ad_id: getParam("ad_id"),
    adset_id: getParam("adset_id"),
    gclid: getParam("gclid"),
    fbclid: getParam("fbclid"),
    session_referrer_url: getParam("session_referrer_url") || referrer || "",
    submission_page_url:
      getParam("submission_page_url") || getFallbackSubmissionPageUrl(),
    utm_source_url: utmSourceUrl,
    var: getParam("var"),
    submitted_time: Math.floor(Date.now() / 1000),
    data_source: "website",
  }
}

const getParam = (key: string) => {
  if (typeof window === "undefined") return ""

  const searchParams = new URLSearchParams(window.location.search)

  const param = parseParam(searchParams.get(key))
  return param || ""
}
